import type { FC } from 'react';
import type { Ply } from '../lib/types';
import { FLUTE_TYPES, PLY_MATERIALS } from '../lib/constants';
import { s } from '../lib/styles';

interface Props {
  ply: Ply;
  index: number;
  onUpdate: (index: number, field: keyof Ply, value: string) => void;
}

const PlyLayer: FC<Props> = ({ ply, index, onUpdate }) => {
  const isFlute = ply.type === 'flute';
  const gsmMissing = !ply.gsm;
  const fluteMissing = isFlute && !ply.fluteType;

  return (
    <div
      style={{
        ...s.plySlot,
        background: isFlute ? '#fffbeb' : '#eff6ff',
        borderColor: isFlute ? '#fde68a' : '#bfdbfe',
      }}
    >
      <div style={s.plyLayerLabel}>
        <span
          style={{
            ...s.plyTypeTag,
            background: isFlute ? '#fef3c7' : '#dbeafe',
            color: isFlute ? '#d97706' : '#2563eb',
          }}
        >
          {isFlute ? '〜 Flute' : '━ Liner'}
        </span>
        <span style={{ color: '#94a3b8', fontSize: 10 }}>
          Layer {index + 1}
          {index === 0 ? ' (outer face)' : ''}
        </span>
      </div>

      <div style={s.plyFields}>
        <div style={s.fieldGroup}>
          <label style={s.label}>Material</label>
          <select
            value={ply.material}
            onChange={(e) => onUpdate(index, 'material', e.target.value)}
            style={s.inputSm}
          >
            {PLY_MATERIALS.map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </div>

        <div style={s.fieldGroup}>
          <label style={s.label}>
            GSM{' '}
            {gsmMissing && (
              <span style={{ color: '#ea580c', fontSize: 9 }}>
                · AI assumes
              </span>
            )}
          </label>
          <input
            type="number"
            value={ply.gsm}
            onChange={(e) => onUpdate(index, 'gsm', e.target.value)}
            placeholder={isFlute ? '120' : '150'}
            style={{
              ...s.inputSm,
              borderColor: gsmMissing ? '#fed7aa' : '#e2e8f0',
            }}
          />
        </div>

        {isFlute && (
          <div style={s.fieldGroup}>
            <label style={s.label}>
              Flute{' '}
              {fluteMissing && (
                <span style={{ color: '#ea580c', fontSize: 9 }}>
                  · AI assumes
                </span>
              )}
            </label>
            <select
              value={ply.fluteType}
              onChange={(e) => onUpdate(index, 'fluteType', e.target.value)}
              style={{
                ...s.inputSm,
                borderColor: fluteMissing ? '#fed7aa' : '#e2e8f0',
              }}
            >
              <option value="">— AI decides —</option>
              {Object.entries(FLUTE_TYPES).map(([k, v]) => (
                <option key={k} value={k}>
                  {k} (×{v.takeup})
                </option>
              ))}
            </select>
          </div>
        )}

        {isFlute && ply.fluteType && (
          <div style={s.fieldGroup}>
            <label style={s.label}>Takeup</label>
            <div style={s.readonlyPill}>
              ×{FLUTE_TYPES[ply.fluteType]?.takeup}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlyLayer;
